import React, { useState, useEffect } from "react";
import { Box, Checkbox, CircularProgress, LinearProgress, Typography } from "@mui/material";
import MobileTopBar from "../components/navigation/MobileTopBar";
import GymCard from "../components/cards/GymCard";
import BookingCard from "../components/cards/BookingCard";
import api from "../components/api";
import MessageBox from "../components/utils/MessageBox";
import { useNavigate } from "react-router-dom";

const Bookings = ({ state, setState, mobile, height, marginTop, location, setLocation }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [frequentGyms, setFrequentGyms] = useState([]);
  const [nearbyGyms, setNearbyGyms] = useState([]);
  const [newGyms, setNewGyms] = useState([]);
  const [popularGyms, setPopularGyms] = useState([]);
  const [permissionStatus, setPermissionStatus] = useState(null); // 'granted', 'denied', or 'prompt'
  //const [locationObtained, setLocationObtained] = useState(false);
  const [locationStatus, setLocationStatus] = useState("verify");
  const navigate = useNavigate();

  useEffect(() => {
    const handlePermissions = async () => {
      const promptShowed = localStorage.getItem("fullpass_location_permission_prompt");
      if (promptShowed != "false") return;
      const permissionSaved = localStorage.getItem("fullpass_location_permission");
      const permission = await checkLocationPermission();

      if (permissionSaved === "granted") {
        if (permission === "granted") {
          localStorage.setItem("fullpass_location_permission", "granted");
          setLocationStatus("granted");
          getLocation();
        } else if (permission === "denied") {
          setLocationStatus("denied");
          localStorage.setItem("fullpass_location_permission", "denied");
        } else {
          setLocationStatus("prompt");
        }
      } else if (permissionSaved === "prompt") {
        if (permission === "denied") {
          setLocationStatus("denied");
          localStorage.setItem("fullpass_location_permission", "denied");
        } else {
          localStorage.setItem("fullpass_location_permission", permission);
          setLocationStatus("granted");
          getLocation();
        }
      } else {
        if (permission === "granted") {
          localStorage.setItem("fullpass_location_permission", "granted");
          setLocationStatus("granted");
          getLocation();
        } else if (permission === "denied") {
          setLocationStatus("denied");
          localStorage.setItem("fullpass_location_permission", "denied");
        } else {
          setLocationStatus("prompt");
        }
      }
    };

    handlePermissions();
  }, []);

  const getGyms = () => {
    if (!location.lat && locationStatus != "denied_accepted") return;
    setLoading(true);
    api.getHomeGyms(
      { lat: location.lat, lon: location.lon },
      (data) => {
        setFrequentGyms(data.frequentGyms);
        setNearbyGyms(data.nearByGyms);
        setNewGyms(data.newGyms);
        setPopularGyms(data.popularGyms);
        setLoading(false);
      },
      (error) => {
        setError(true);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (locationStatus == "denied_accepted") {
      localStorage.setItem("fullpass_location_permission_prompt", true);
      getGyms();
    }
  }, [locationStatus]);

  useEffect(() => {
    if (location.lat) {
      setLocationStatus("obtained");
      getGyms();
    }
  }, [location]);

  const getBookings = () => {
    setLoading(true);
    api.getActiveBookings(
      (data) => {
        setBookings(data);
        setLoading(false);
        getGyms();
      },
      (error) => {
        setError(true);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    getBookings();
    const promptShowed = localStorage.getItem("fullpass_location_permission_prompt");
    if (promptShowed != "false") {
      setLocationStatus("denied_accepted");
    }
  }, []);

  const getLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
        setError(null);
        setLocationStatus("obtained");
      },
      (err) => {
        setError("Failed to retrieve location. Make sure location services are enabled.");
        setLocationStatus("denied");
        console.log("Location denied");
      }
    );
  };

  const handleRequestPermission = () => {
    localStorage.setItem("fullpass_location_permission", "prompt");
    getLocation();
  };

  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (result.state === "granted") {
            getLocation();
          } else if (result.state === "prompt") {
            //getLocation();
            setError("Por favor, permitir el acceso a la ubicación para mostrar gimnasios cercanos.");
          }
          result.onchange = () => setPermissionStatus(result.state);
        })
        .catch(() => setError("Permission API is not supported in this browser."));
    } else {
      setError("Permission API is not supported in this browser.");
    }
  }, []);

  const checkLocationPermission = async () => {
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: "geolocation" });
        return result.state;
      } catch {
        return "denied";
      }
    } else {
      return "denied";
    }
  };

  const formatNumberWithDots = (number) => {
    const numberString = number.toString();
    const parts = [];
    for (let i = numberString.length - 1; i >= 0; i -= 3) {
      const part = numberString.substring(Math.max(0, i - 2), i + 1);
      parts.unshift(part);
    }
    return parts.join(".");
  };

  const round = (number) => Math.round(number * 10) / 10;

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `a ${round(distance) * 1000} m`;
    } else {
      return `a ${round(distance)} km`;
    }
  };

  const formatTwoDigits = (number) => {
    return number < 10 ? `0${number}` : number;
  };

  return (
    <Box
      sx={{
        height: { height },
        overflow: "scroll",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        padding: "0px 10px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "center",
      }}
    >
      {mobile > 0 && <MobileTopBar onBack={() => setState(0)}>Reservar</MobileTopBar>}
      {locationStatus == "prompt" && (
        <MessageBox acceptText={"Permitir"} onAccept={handleRequestPermission}>
          <Typography
            style={{
              fontWeight: "500",
              fontSize: "24px",
              color: "white",
              textAlign: "justify",
              lineHeight: "1.2",
            }}
          >
            A continuación, por favor <span style={{ color: "#FF9700" }}>permite</span> a Full Pass acceder a tu{" "}
            <span style={{ color: "#FF9700" }}>ubicación</span> para mostrarte los gimnasios cercanos.
          </Typography>
        </MessageBox>
      )}
      {locationStatus == "denied" && (
        <MessageBox
          acceptText="Aceptar"
          onAccept={() => {
            setLocationStatus("denied_accepted");
          }}
        >
          <Typography
            style={{
              fontWeight: "500",
              fontSize: "24px",
              color: "white",
              textAlign: "justify",
              lineHeight: "1.2",
            }}
          >
            Error al obtener tu ubicación. Por favor, <span style={{ color: "#FF9700" }}>permite</span> a Full Pass
            acceder a tu <span style={{ color: "#FF9700" }}>ubicación</span> para poder mostrarte los gimnasios cercanos
            a vos y mejorar tu experiencia.
          </Typography>
        </MessageBox>
      )}

      {(locationStatus == "obtained" || locationStatus == "denied_accepted") && (
        <>
          <Box
            style={{
              maxWidth: "700px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography
              style={{
                width: "100%",
                textAlign: "left",
                color: "white",
                fontWeight: "500",
                fontSize: "24px",
                marginLeft: "10px",
              }}
            >
              Mis reservas
            </Typography>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {bookings.length == 0 ? (
                <Typography
                  style={{
                    width: "100%",
                    textAlign: "left",
                    color: "white",
                    fontWeight: "500",
                    fontSize: "18px",
                    marginLeft: "10px",
                    color: "#9D9D9D",
                  }}
                >
                  No tenés reservas vigentes
                </Typography>
              ) : (
                bookings.map((booking) => (
                  <BookingCard
                    key={booking.gymId}
                    gymName={booking.gymName}
                    bookingTime={`${booking.day == "TODAY" ? "HOY" : "MAÑANA"} ${booking.hour}:${formatTwoDigits(
                      booking.minutes
                    )}`}
                    gymIcon={booking.iconLowResUrl}
                    onClick={() => navigate(`/gimnasio/${booking.gymId}`)}
                  />
                ))
              )}
            </Box>
            {frequentGyms.length > 0 && (
              <>
                <Typography
                  style={{
                    width: "100%",
                    textAlign: "left",
                    color: "white",
                    fontWeight: "500",
                    fontSize: "24px",
                    marginLeft: "10px",
                  }}
                >
                  Gimnasios frecuentes
                </Typography>

                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  {frequentGyms.map((gym) => (
                    <GymCard
                      key={gym.id}
                      gymName={gym.name}
                      gymAddress={gym.address}
                      gymDistance={gym.distance && formatDistance(gym.distance)}
                      gymIcon={gym.iconLowResUrl}
                      onClick={() => navigate(`/gimnasio/${gym.id}`)}
                    />
                  ))}
                </Box>
              </>
            )}
            {nearbyGyms.length > 0 && (
              <>
                <Typography
                  style={{
                    width: "100%",
                    textAlign: "left",
                    color: "white",
                    fontWeight: "500",
                    fontSize: "24px",
                    marginLeft: "10px",
                  }}
                >
                  Gimnasios cercanos
                </Typography>

                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  {nearbyGyms.map((gym) => (
                    <GymCard
                      key={gym.id}
                      gymName={gym.name}
                      gymAddress={gym.address}
                      gymDistance={gym.distance && formatDistance(gym.distance)}
                      gymIcon={gym.iconLowResUrl}
                      onClick={() => navigate(`/gimnasio/${gym.id}`)}
                    />
                  ))}
                </Box>
              </>
            )}
            {popularGyms.length > 0 && (
              <>
                <Typography
                  style={{
                    width: "100%",
                    textAlign: "left",
                    color: "white",
                    fontWeight: "500",
                    fontSize: "24px",
                    marginLeft: "10px",
                  }}
                >
                  Gimnasios populares
                </Typography>

                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  {popularGyms.map((gym) => (
                    <GymCard
                      key={gym.id}
                      gymName={gym.name}
                      gymAddress={gym.address}
                      gymDistance={gym.distance && formatDistance(gym.distance)}
                      gymIcon={gym.iconLowResUrl}
                      onClick={() => navigate(`/gimnasio/${gym.id}`)}
                    />
                  ))}
                </Box>
              </>
            )}
            {newGyms.length > 0 && (
              <>
                <Typography
                  style={{
                    width: "100%",
                    textAlign: "left",
                    color: "white",
                    fontWeight: "500",
                    fontSize: "24px",
                    marginLeft: "10px",
                  }}
                >
                  Nuevos en Full Pass
                </Typography>

                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  {newGyms.map((gym) => (
                    <GymCard
                      key={gym.id}
                      gymName={gym.name}
                      gymAddress={gym.address}
                      gymDistance={formatDistance(gym.distance)}
                      gymIcon={gym.iconLowResUrl}
                      onClick={() => navigate(`/gimnasio/${gym.id}`)}
                    />
                  ))}
                </Box>
              </>
            )}
            {mobile > 0 && (
              <div
                style={{
                  width: "100%",
                  height: "70px",
                  color: "rgba(0,0,0,0)",
                }}
              >
                -
              </div>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Bookings;
